/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { LngLat, RouteResult } from "../../utils/pathfinding/fairwayRouting";
import { computeFairwayRoute } from "../../utils/pathfinding/fairwayRouting";

type Status = "idle" | "loading" | "ready" | "error";

interface FairwayRoutingState {
  startClick: LngLat | null;
  endClick: LngLat | null;
  route: RouteResult | null;
  status: Status;
  error: string | null;
  onMapClick: (lngLat: LngLat) => void;
  clear: () => void;
  swap: () => void;
}

const Ctx = createContext<FairwayRoutingState | null>(null);

export function FairwayRoutingProvider({ children }: { children: React.ReactNode }) {
  const [startClick, setStartClick] = useState<LngLat | null>(null);
  const [endClick, setEndClick] = useState<LngLat | null>(null);
  const [route, setRoute] = useState<RouteResult | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const clear = useCallback(() => {
    setStartClick(null);
    setEndClick(null);
    setRoute(null);
    setStatus("idle");
    setError(null);
  }, []);

  const swap = useCallback(() => {
    if (!startClick || !endClick) return;
    setStartClick(endClick);
    setEndClick(startClick);
    // route will be recomputed by effect below
  }, [startClick, endClick]);

  const onMapClick = useCallback(
    (lngLat: LngLat) => {
      setError(null);

      // 1st click: set start
      if (!startClick) {
        setStartClick(lngLat);
        setEndClick(null);
        setRoute(null);
        setStatus("idle");
        return;
      }

      // 2nd click: set end and compute
      if (!endClick) {
        setEndClick(lngLat);
        return;
      }

      // 3rd click: reset and start a new route
      setStartClick(lngLat);
      setEndClick(null);
      setRoute(null);
      setStatus("idle");
    },
    [startClick, endClick]
  );

  // Compute route whenever both clicks exist
  React.useEffect(() => {
    let cancelled = false;
    if (!startClick || !endClick) return;

    setStatus("loading");
    setError(null);

    computeFairwayRoute(startClick, endClick)
      .then((res) => {
        if (cancelled) return;
        setRoute(res);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRoute(null);
        setStatus("error");
        setError(e instanceof Error ? e.message : "Routing failed");
      });

    return () => {
      cancelled = true;
    };
  }, [startClick, endClick]);

  const value = useMemo<FairwayRoutingState>(
    () => ({
      startClick,
      endClick,
      route,
      status,
      error,
      onMapClick,
      clear,
      swap,
    }),
    [startClick, endClick, route, status, error, onMapClick, clear, swap]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFairwayRouting(): FairwayRoutingState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useFairwayRouting must be used within FairwayRoutingProvider");
  return v;
}
